import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useQuery, keepPreviousData} from '@github-ui/react-query'
import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  ServerUnavailableError,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {sendStats} from '@github-ui/stats'
import {useEffect, useMemo, useState} from 'react'
import type {CIFinding, DedupedCIFinding} from './ci-findings-types'
import {deduplicateCIFindings} from './deduplicate-ci-findings'

type CIFindingsAPIResponse = {
  ciFindings: CIFinding[]
  checkRunId?: number
  checkSuiteId?: number
  pullRequestId?: number
  headSha?: string
  availableHeadShas?: string[]
}

type CIFindingsData = {
  findings: CIFinding[]
  headSha: string | undefined
}

export const POLL_INTERVAL_MS = 10_000
export const MAX_POLL_ATTEMPTS = 30 // Stop polling after ~5 minutes (30 × 10s)
const POLL_TIMEOUT_MS = MAX_POLL_ATTEMPTS * POLL_INTERVAL_MS
const EMPTY_FINDINGS: CIFinding[] = []

export function useCIFindings({
  enabled = true,
  pollingTimeoutMs = POLL_TIMEOUT_MS,
  apiUrl: apiUrlOverride,
}: {enabled?: boolean; pollingTimeoutMs?: number; apiUrl?: string} = {}): {
  findings: DedupedCIFinding[]
  headSha: string | undefined
  isPolling: boolean
  isError: boolean
} {
  const pageDataUrl = usePageDataUrl(PageData.ciFindings)
  const apiURL = apiUrlOverride ?? pageDataUrl
  const [pollingExpired, setPollingExpired] = useState(false)

  const {data, isError} = useQuery({
    queryKey: ['ciFindings', apiURL],
    queryFn: async (): Promise<CIFindingsData> => {
      const response = await fetchWithErrorHandling(apiURL)
      // Short-circuit on 5xx before parsing — proxies often return non-JSON error pages
      if (!response.ok && response.status >= 500) {
        throw new ServerUnavailableError(response.status)
      }
      const json = (await parseJSONWithBetterErrors(response)) as CIFindingsAPIResponse & {error?: string}
      throwErrorsIfBadResponse(response, json)
      return {
        findings: json.ciFindings ?? [],
        headSha: json.headSha,
      }
    },
    staleTime: 30_000,
    // Only retry transient server/network errors, not 4xx client errors
    retry: (failureCount, error) => {
      if (failureCount >= 2) return false
      return (
        error instanceof ServerUnavailableError ||
        error.name === 'FetchRequestError' ||
        error.name === 'JSONParseFetchError' ||
        error.name === 'TypeError'
      )
    },
    enabled,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    refetchInterval: query => {
      if (query.state.data && query.state.data.findings.length > 0) return false
      if (query.state.status === 'error') return false
      if (pollingExpired) return false
      return POLL_INTERVAL_MS
    },
  })

  const rawFindings = data?.findings ?? EMPTY_FINDINGS
  const shouldPoll = enabled && rawFindings.length === 0 && !isError

  // Reset pollingExpired when polling conditions clear (set-state-during-render)
  if (!shouldPoll && pollingExpired) {
    setPollingExpired(false)
  }

  useEffect(() => {
    if (!shouldPoll) return
    const timer = setTimeout(() => setPollingExpired(true), pollingTimeoutMs)
    return () => clearTimeout(timer)
  }, [shouldPoll, pollingTimeoutMs])

  useEffect(() => {
    if (isError) {
      sendStats({incrementKey: 'CI_FINDINGS_FETCH_ERROR'})
    }
  }, [isError])

  // Polling is active when enabled, no findings, no error, and within the polling window
  const isPolling = shouldPoll && !pollingExpired

  const findings = useMemo(() => deduplicateCIFindings(rawFindings), [rawFindings])

  return {
    findings,
    headSha: data?.headSha,
    isPolling,
    isError,
  }
}
